/**
 * CollaborationStatus - Shows connection status and active collaborators
 *
 * Displays a small indicator showing real-time collaboration status:
 * - Green dot: Connected to real-time server
 * - Yellow dot: Reconnecting
 * - Gray dot: Disconnected
 *
 * Also shows avatars of other active users in the workspace.
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { useCollaborationContext } from '../../lib/collaboration';
import { useAuth } from '../../lib/auth/AuthProvider';
import { PresenceIndicator, getUserColor, getUserInitials } from './PresenceIndicator';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Wifi, WifiOff, Radio } from 'lucide-react';

export function CollaborationStatus() {
  const { connectionState, isConnected, activeUsers } = useCollaborationContext();
  const { user } = useAuth();

  // Filter out current user from active users
  const otherUsers = Array.from(activeUsers.values()).filter(
    (u) => u.userId !== user?.userId
  );

  const statusConfig = {
    connected: {
      color: 'bg-green-500',
      icon: Wifi,
      label: 'Connected',
    },
    connecting: {
      color: 'bg-yellow-500',
      icon: Radio,
      label: 'Connecting...',
    },
    reconnecting: {
      color: 'bg-yellow-500 animate-pulse',
      icon: Radio,
      label: 'Reconnecting...',
    },
    disconnected: {
      color: 'bg-gray-400',
      icon: WifiOff,
      label: 'Offline',
    },
  };

  const status = statusConfig[connectionState];
  const StatusIcon = status.icon;

  return (
    <Tooltip.Provider delayDuration={300}>
      <div className="flex items-center gap-2">
        {/* Active Users */}
        {otherUsers.length > 0 && (
          <div className="flex items-center">
            <PresenceIndicator
              users={otherUsers.map((u) => ({
                userId: u.userId,
                userName: u.userName,
              }))}
              size="sm"
              maxDisplayed={3}
            />
          </div>
        )}

        {/* Connection Status Indicator */}
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-accent transition-colors cursor-default">
              <div className={cn('w-2 h-2 rounded-full', status.color)} />
              <StatusIcon className="w-4 h-4 text-muted-foreground" />
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="z-50 px-3 py-2 text-sm bg-popover text-popover-foreground rounded-md shadow-md border border-border max-w-xs"
              sideOffset={5}
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className={cn('w-2 h-2 rounded-full', status.color)} />
                  <span className="font-medium">{status.label}</span>
                </div>
                {otherUsers.length > 0 && (
                  <div className="pt-1 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">
                      {otherUsers.length} collaborator{otherUsers.length !== 1 ? 's' : ''} online:
                    </p>
                    <div className="space-y-1">
                      {otherUsers.slice(0, 5).map((u) => (
                        <div key={u.userId} className="flex items-center gap-2 text-xs">
                          <div
                            className={cn(
                              'w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px]',
                              getUserColor(u.userId)
                            )}
                          >
                            {getUserInitials(u.userName)}
                          </div>
                          <span className="truncate">{u.userName || 'Anonymous'}</span>
                          {u.editingRequestId && (
                            <span className="text-muted-foreground">(editing)</span>
                          )}
                        </div>
                      ))}
                      {otherUsers.length > 5 && (
                        <p className="text-xs text-muted-foreground">
                          +{otherUsers.length - 5} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Tooltip.Arrow className="fill-popover" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </div>
    </Tooltip.Provider>
  );
}
